import FilesIcons from "@common-components/files-icons/filesIcons";
import { Tooltip } from "@mui/material";
import { pickerDialogActions } from "@common-reducers/PickerDialogReducer";
import { mediaManager } from "@common-reducers/MediaManagerReducer";
import { handleUpload } from "@common-services/upload";
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { RiCloseCircleFill } from "react-icons/ri";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { ReactComponent as AddIcon } from "@common-assets/add.svg";
import Dropzone from "./dropzone";
import classes from "./files_cell.module.scss";
import { ReactComponent as FilesIcon } from "./small-file.svg";
import { CellProps } from "../cell-interface";
import moment from "moment";
import CircularProgress from '@mui/material/CircularProgress';
import { namespace } from "@common-config/constants";
import { useTranslation } from "react-i18next";

type FileVersion = {
  url: string;
  type: string;
  filename: string;
  updatedAt: string;
  version: number;
};

type FileDetails = FileVersion & {
  versions: FileVersion[];
};

export default function FilesCell({
  cellValue: currentFiles,
  taskId,
  columnId,
  readOnly,
  onUpdate,
  togglePicker,
  mockValue,
}: CellProps) {
  const { t } = useTranslation(namespace);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useAppDispatch()
  const anchorId = `files_${taskId}`;
  const [loading, showLoader] = useState(false);
  function addFiles(e: MouseEvent<HTMLDivElement>) {
    if (readOnly) return;
    e.stopPropagation();
    if (!fileRef.current) return;
    fileRef.current.value = "";
    fileRef.current.click();
  }

  function clearFile(e: MouseEvent<SVGElement>) {
    e.stopPropagation();
    onUpdate(undefined);
  }
  const [delayHandler, setDelayHandler] = useState<ReturnType<typeof setTimeout> | null>(null);

  function hoverFile() {
    if (currentFiles.length === 1) return;
    setDelayHandler(
      setTimeout(() => {
        togglePicker({
          anchorId: anchorId,
          pickerType: 'files'
        })
      }, 300)
    );
  }

  const handleMouseLeave = () => {
    if (delayHandler) clearTimeout(delayHandler);
  };

  useEffect(() => {
    return () => {
      if (delayHandler) clearTimeout(delayHandler);
    };
  }, [delayHandler]);

  function openMediaManager(e: MouseEvent<HTMLSpanElement>, i: number) {
    e.stopPropagation();
    const currentFilesAndIndex: {
      index: number;
      shown: boolean;
      taskId: string | undefined;
      columnId: string | undefined;
      isViewOnly: boolean;
      filesTotal?: FileDetails[];
    } = {
      index: i,
      shown: true,
      taskId,
      columnId,
      isViewOnly: false,
    };
    if (readOnly) {
      currentFilesAndIndex.filesTotal = currentFiles;
      currentFilesAndIndex.isViewOnly = true;
    }
    dispatch(
      mediaManager.setSelectedCellFilesAndCurrentindex(currentFilesAndIndex)
    );
    dispatch(pickerDialogActions.closePickerDialog());
  }

  const getUniqeAndDuplicateFiles = useCallback((filesToUpload: FileDetails[]) => {
    let isFileExists: FileDetails | undefined;
    let filesToDecideNewVersionOrNewFile: FileDetails[] = [];
    let uniqueFilesToUpload: FileDetails[] = [];
    for (let i = 0; i < filesToUpload.length; i++) {
      const fileName: string = filesToUpload[i].filename;
      isFileExists = currentFiles?.find(({ filename }: { filename: string }) => filename === fileName);
      if (isFileExists) {
        filesToDecideNewVersionOrNewFile = [...filesToDecideNewVersionOrNewFile, filesToUpload[i]]
      } else {
        uniqueFilesToUpload = [...uniqueFilesToUpload, filesToUpload[i]];
      }
    }
    return { uniqueFilesToUpload, filesToDecideNewVersionOrNewFile };
  }, [currentFiles])

  const updateUniqueFiles = useCallback((filesToUpload: FileDetails[]) => {
    const files = currentFiles ? currentFiles.concat(filesToUpload) : filesToUpload;
    onUpdate(files);
  }, [currentFiles, onUpdate])

  const uploadFiles = useCallback(async (files: FileList | File[] | null): Promise<FileDetails[] | undefined> => {
    if (files) {
      const filesArray = [...files];
      const promises = filesArray.map(async (file): Promise<FileDetails> => {
        return new Promise<FileDetails>((resolve, reject) => {
          const reader = new FileReader();
          const mimeType: string = file.type;
          const filename: string = file.name;
          reader.addEventListener("load", async () => {
            const base64file = reader.result as string;
            const urlLink = await uploadFile(base64file, mimeType);
            const updatedAt = moment().format('DD/MM/YYYY HH:mm:ss');
            const fileDetails: FileDetails = {
              url: urlLink ?? "",
              type: mimeType,
              filename,
              updatedAt,
              version: 1,
              versions: [{ url: urlLink ?? "", type: mimeType, filename, updatedAt, version: 1 }],
            };
            resolve(fileDetails);
          });
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      const currentFilesUploaded = await Promise.all(promises);
      return currentFilesUploaded;
    }
  }, [])

  const updateState = useCallback((filesToDecideNewVersionOrNewFile: FileDetails[]) => {
    dispatch(
      mediaManager.setFilesToDecideNewVersionOrNewFile({ taskId, columnId, filesToDecideNewVersionOrNewFile })
    );
  }, [columnId, dispatch, taskId])

  const onNewFilesAdded = useCallback(async (newFiles: FileList | File[] | null) => {
    showLoader(true);
    const fileListUploaded = await uploadFiles(newFiles);
    const { uniqueFilesToUpload, filesToDecideNewVersionOrNewFile } = getUniqeAndDuplicateFiles(fileListUploaded ?? []);
    if (uniqueFilesToUpload.length > 0) {
      updateUniqueFiles(uniqueFilesToUpload);
    }
    if (filesToDecideNewVersionOrNewFile.length > 0) {
      updateState(filesToDecideNewVersionOrNewFile);
    }
    showLoader(false);
    if (fileRef?.current) {
      fileRef.current.value = "";
    }
  }, [getUniqeAndDuplicateFiles, uploadFiles, updateUniqueFiles, updateState])

  const onDrop = useCallback(
    (droppedFiles: File[]) => {
      onNewFilesAdded(droppedFiles);
    },
    [onNewFilesAdded]);

  const uploadFile = async (base64file: string, mimeType: string): Promise<string | undefined> => {
    try {
      const response = await handleUpload(base64file, mimeType) as { uploadResponse?: { url?: string }, image_url?: string };
      if (response?.uploadResponse?.url) {
        return response.image_url;
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div
      className={classes.filesContainer}
      style={{ width: mockValue && 'auto' }}
      onClick={(e) => {
        addFiles(e);
      }}
      id={`files_${taskId}`}
    >
      <Dropzone onDrop={onDrop} />
      {(currentFiles && !readOnly) && (
        <Tooltip title={t('filesCell.addFiles')} arrow placement="top" disableInteractive>
          <AddIcon
            className={[classes.addIcon, classes.addWithFiles].join(" ")}
          />
        </Tooltip>
      )}
      <div className={classes.filesCell}>
        {(!currentFiles && !loading && !readOnly) && (
          <>
            <AddIcon className={classes.addIcon} />
            <FilesIcon className={classes.filesIcon} />
          </>
        )}
        <input
          onChange={(e) => {
            onNewFilesAdded(e.target.files);
          }}
          ref={fileRef}
          type="file"
          multiple={true}
          hidden
          readOnly={readOnly}
        />
        {loading && (
          <CircularProgress style={{ color: "var(--tasks-button-hover-bg)" }} size={18} />
        )}
        {!loading && currentFiles?.slice(0, 3).map((file: FileDetails, i: number) => {
          return (
            <span key={i} className={classes.fileIcon}
              onClick={(e) => {
                openMediaManager(e, i);
              }}
              onMouseEnter={hoverFile}
              onMouseLeave={handleMouseLeave}
            >
              <FilesIcons file={file} />
            </span>
          );
        })}
      </div>
      {currentFiles?.length > 3 && (
        <div
          onMouseEnter={hoverFile}
          onMouseLeave={handleMouseLeave}
          className={classes.extraFiles}>
          +{currentFiles.length - 3}
        </div>
      )}
      {(currentFiles?.length === 1 && !readOnly) && (
        <RiCloseCircleFill
          onClick={(e) => {
            clearFile(e);
          }}
          className={classes.clearIcon}
        />
      )}
    </div>
  );
}
